// RoleBasedSidebar.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLocalization } from "../contexts/LocalizationContext";
import {
  LayoutDashboard,
  ArrowLeftRight,
  Wallet,
  FileText,
  ChevronRight,
  ChevronLeft,
  Users,
  CreditCard,
  BarChart3,
  Ticket,
  LogOut,
  BookOpen,
  FileCode,
  Receipt,
  History,
  DollarSign,
  Settings,
  UserCircle,
  ChevronDown,
  Bot,
  Banknote,
  Menu,
} from "lucide-react";
import { useWithdrawalEligibility } from "../hooks/useWithdrawalEligibility";
import { subscriptionService } from "../services/subscriptionService";

interface RoleBasedSidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onNavigate?: () => void;
  logout?: () => void;
  isCollapsed?: boolean;
  onToggle?: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: any;
  badge?: string | null;
  hasSubmenu?: boolean;
  submenu?: SubMenuItem[];
  path?: string;
  section?: string;
}

interface SubMenuItem {
  id: string;
  label: string;
  icon: any;
  path: string;
}

const RoleBasedSidebar: React.FC<RoleBasedSidebarProps> = ({
  activeTab,
  setActiveTab,
  onNavigate,
  logout,
  isCollapsed = false,
  onToggle,
}) => {
  const { user, merchantProfile, loading } = useAuth();
  const { t } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});

  const { data: eligibility, isLoading: isLoadingEligibility } = useWithdrawalEligibility();
  const hasWithdrawalAccess = eligibility?.has_withdrawal_access || false;

  const [hasCardAccess, setHasCardAccess] = useState(false);
  const [hasMobileMoneyAccess, setHasMobileMoneyAccess] = useState(true); // default true; refined async
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);

  // Betting / gambling sectors are excluded from cashout for regulatory and
  // operational reasons (their outflows follow a customer-payout logic, not a
  // merchant cashout). Defensive UI check on top of backend eligibility.
  const isBettingMerchant = (() => {
    const sector = merchantProfile?.institution?.sector?.toLowerCase() || "";
    if (!sector) return false;
    return ["bet", "gam", "paris", "jeu", "casino", "lott", "wager"].some(kw =>
      sector.includes(kw)
    );
  })();

  useEffect(() => {
    const currentPath = location.pathname;
    const menuItems = getMenuItems();

    for (const item of menuItems) {
      if (item.path === currentPath) {
        setActiveTab(item.id);
        break;
      }

      if (item.submenu) {
        for (const subItem of item.submenu) {
          if (subItem.path === currentPath || currentPath.startsWith(subItem.path)) {
            setActiveTab(item.id);
            if (!isCollapsed) {
              setExpandedMenus(prev => ({
                ...prev,
                [item.id]: true
              }));
            }
            break;
          }
        }
      }
    }
  }, [location.pathname, setActiveTab, isCollapsed]);

  useEffect(() => {
    const checkSubscriptions = async () => {
      if (!user?.merchant_code) {
        setHasCardAccess(false);
        setHasMobileMoneyAccess(false);
        return;
      }

      setIsCheckingAccess(true);
      try {
        const methods = await subscriptionService.getActivePaymentMethods();
        setHasCardAccess(methods.has_card);
        setHasMobileMoneyAccess(methods.has_mobile_money);
      } catch (error) {
        console.error("Error checking subscriptions:", error);
        setHasCardAccess(false);
        // Keep mobile money optimistically true if the check fails: most
        // merchants are mobile money first, hiding everything on a network
        // hiccup would be worse than showing menus that 404 on click.
        setHasMobileMoneyAccess(true);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    if (user?.merchant_code) {
      checkSubscriptions();
    }
  }, [user?.merchant_code]);

  const handleNavigation = (path: string, tabId?: string, isSubmenu: boolean = false) => {
    if (tabId) {
      setActiveTab(tabId);
    }
    navigate(path);
    if (onNavigate) {
      onNavigate();
    }
    if (isSubmenu && onNavigate) {
      setTimeout(() => {
        onNavigate();
      }, 100);
    }
  };

  const toggleSubmenu = (menuId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (isCollapsed) {
      const menuItem = getMenuItems().find(item => item.id === menuId);
      if (menuItem?.submenu && menuItem.submenu.length > 0) {
        handleNavigation(menuItem.submenu[0].path, menuId, true);
      }
      return;
    }

    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const determineRole = (): string => {
    if (!user) return "user";

    const validRoles = ["merchant_admin", "merchant_finance", "merchant_support"];
    if (user.role && validRoles.includes(user.role)) {
      return user.role;
    }

    let niveauNum: number;
    if (typeof user.niveau === 'number') {
      niveauNum = user.niveau;
    } else if (typeof user.niveau === 'string') {
      niveauNum = parseInt(user.niveau, 10);
      if (isNaN(niveauNum)) {
        niveauNum = 0;
      }
    } else {
      niveauNum = 0;
    }

    if (niveauNum === 0) return "merchant_admin";
    if (niveauNum === 1) return "merchant_finance";
    if (niveauNum === 2) return "merchant_support";
    return "user";
  };

  const getMenuItems = (): MenuItem[] => {
    const baseItems: MenuItem[] = [
      {
        id: "dashboard",
        label: t("nav.dashboard"),
        icon: LayoutDashboard,
        path: "/dashboard",
        section: "main"
      },
    ];

    if (!user) {
      return baseItems;
    }

    const roleKey = determineRole();

    // ─── Cards product section (subscription-gated) ───────────────────
    const cardItems: MenuItem[] = hasCardAccess ? [
      {
        id: "card-balances",
        label: t("nav.virtualCards"),
        icon: CreditCard,
        path: "/cards/balances",
        section: "cards",
      },
      {
        id: "card-transactions",
        label: t("nav.transactions"),
        icon: DollarSign,
        path: "/cards/transactions",
        section: "cards",
      },
      ...(roleKey === "merchant_finance" ? [{
        id: "card-history",
        label: t("nav.walletHistory"),
        icon: History,
        path: "/cards/history",
        section: "cards",
      }] : []),
      ...(roleKey === "merchant_admin" && !isBettingMerchant ? [{
        id: "card-withdraw",
        label: t("nav.cardCashout") || "Card Cashout",
        icon: Banknote,
        path: "/cards/withdraw",
        section: "cards",
      }] : []),
    ] : [];

    // ─── Mobile Money product section (subscription-gated) ────────────
    const mobileMoneyAdminItems: MenuItem[] = hasMobileMoneyAccess ? [
      {
        id: "balances",
        label: t("nav.balances"),
        icon: Wallet,
        path: "/balances",
        section: "mobile_money",
      },
      {
        id: "transfers",
        label: t("nav.transfers"),
        icon: ArrowLeftRight,
        path: "/transfers",
        section: "mobile_money",
      },
      // Cashout is admin-only and excluded for betting merchants.
      // Backend eligibility (hasWithdrawalAccess) is the authoritative gate;
      // this also adds a defensive sector check for early UI hiding.
      ...(hasWithdrawalAccess && !isBettingMerchant ? [{
        id: "withdrawal",
        label: t("nav.withdrawal"),
        icon: Banknote,
        path: "/withdrawal",
        section: "mobile_money",
      }] : []),
    ] : [];

    const roleSpecificItems: Record<string, MenuItem[]> = {
      merchant_admin: [
        ...mobileMoneyAdminItems,
        ...cardItems,
        {
          id: "reports",
          label: t("nav.reports"),
          icon: FileText,
          hasSubmenu: true,
          section: "analytics",
          submenu: [
            {
              id: "overview",
              label: "Overview",
              icon: Receipt,
              path: "/reports/overview"
            },
            {
              id: "transactions",
              label: "Transactions",
              icon: FileCode,
              path: "/reports/transactions"
            },
          ]
        },
        {
          id: "team",
          label: t("nav.team"),
          icon: Users,
          path: "/team",
          section: "management"
        },
        ...(!isBettingMerchant ? [{
          id: "cashout-settings",
          label: t("nav.cashoutSettings") || "Paramètres de décaissement",
          icon: Settings,
          path: "/cashout-settings",
          section: "management",
        }] : []),
        {
          id: "tickets",
          label: t("nav.tickets"),
          icon: Ticket,
          path: "/tickets",
          section: "management"
        },
        {
          id: "account",
          label: t("nav.account"),
          icon: CreditCard,
          path: "/account",
          section: "management"
        },
      ],
      merchant_finance: [
        ...(hasMobileMoneyAccess ? [
          {
            id: "balances",
            label: t("nav.balances"),
            icon: Wallet,
            path: "/balances",
            section: "mobile_money",
          },
          {
            id: "transfers",
            label: t("nav.transfers"),
            icon: ArrowLeftRight,
            path: "/transfers",
            section: "mobile_money",
          },
        ] : []),
        ...cardItems,
        {
          id: "reports",
          label: t("nav.reports"),
          icon: FileText,
          hasSubmenu: true,
          section: "analytics",
          submenu: [
            {
              id: "overview",
              label: "Overview",
              icon: Receipt,
              path: "/reports/overview"
            },
            {
              id: "transactions",
              label: "Transactions",
              icon: FileCode,
              path: "/reports/transactions"
            },
          ]
        },
        {
          id: "analytics",
          label: t("nav.analytics"),
          icon: BarChart3,
          path: "/analytics",
          section: "analytics"
        },
        {
          id: "tickets",
          label: t("nav.tickets"),
          icon: Ticket,
          path: "/tickets",
          section: "management"
        },
      ],
      merchant_support: [
        {
          id: "reports",
          label: t("nav.reports"),
          icon: FileText,
          path: "/reports/overview",
          section: "analytics"
        },
        {
          id: "tickets",
          label: t("nav.tickets"),
          icon: Ticket,
          path: "/tickets",
          section: "management"
        },
      ],
    };

    const specificItems = roleSpecificItems[roleKey] || [];
    return [...baseItems, ...specificItems];
  };

  const menuItems = getMenuItems();

  // Group items by section for visual separation
  const groupedItems = () => {
    const groups: { section: string; items: MenuItem[] }[] = [];
    let currentSection = "";

    menuItems.forEach(item => {
      const section = item.section || "main";
      if (section !== currentSection) {
        currentSection = section;
        groups.push({ section, items: [item] });
      } else {
        groups[groups.length - 1].items.push(item);
      }
    });

    return groups;
  };

  const sectionLabels: Record<string, string> = {
    main: t("sidebar.section.overview") || "OVERVIEW",
    mobile_money: t("sidebar.section.mobileMoney") || "MOBILE MONEY",
    cards: t("sidebar.section.cards") || "CARDS",
    finance: t("sidebar.section.finance") || "FINANCE",
    analytics: t("sidebar.section.analytics") || "ANALYTICS",
    management: t("sidebar.section.management") || "MANAGEMENT",
  };

  if (loading) {
    return (
      <div className={`
        relative h-full flex flex-col items-center justify-center transition-all duration-500
        ${isCollapsed ? 'w-20' : 'w-64'}
        bg-[#0C111D]
        border-r border-white/[0.06]
        overflow-hidden
      `}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-xs text-slate-500">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const displayedRole = determineRole();
  const roleDisplayText = displayedRole === "merchant_admin" ? t("role.admin") :
                         displayedRole === "merchant_finance" ? t("role.finance") :
                         displayedRole === "merchant_support" ? t("role.support") : t("role.user");

  const roleColor = displayedRole === "merchant_admin" ? "text-indigo-600 bg-indigo-50 border-indigo-200" :
                    displayedRole === "merchant_finance" ? "text-teal-600 bg-teal-50 border-teal-200" :
                    displayedRole === "merchant_support" ? "text-amber-600 bg-amber-50 border-amber-200" :
                    "text-slate-500 bg-slate-50 border-slate-200";

  return (
    <div className={`
      relative h-full flex flex-col transition-all duration-300 ease-in-out
      ${isCollapsed ? 'w-20' : 'w-64'}
      bg-white
      border-r border-slate-200
      overflow-hidden
    `}>
      {/* Logo Section */}
      <div className="px-4 pt-4 pb-3 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img
              src="/LOGO_MOKO_fd_vert.png"
              alt="MOKO Afrika"
              className="h-9 w-9 rounded-lg flex-shrink-0 shadow-md object-cover"
            />

            {!isCollapsed && (
              <div className="flex flex-col leading-tight">
                <span className="text-[13px] font-bold text-slate-900 tracking-tight">
                  MOKO AFRIKA
                </span>
                <span className="text-[9px] text-indigo-500 font-semibold tracking-[0.15em] uppercase">
                  Merchant Suite
                </span>
              </div>
            )}
          </div>

          {onToggle && (
            <button
              onClick={onToggle}
              className="p-1.5 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
              title={isCollapsed ? t("sidebar.expand") : t("sidebar.collapse")}
            >
              <Menu className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>


      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 pt-6 pb-2 relative">
        {groupedItems().map((group, groupIndex) => {
          const sectionKey = group.section || "main";
          const sectionLabel = sectionLabels[sectionKey] || "";
          return (
          <div key={groupIndex} className="mb-1">
            {/* Section label */}
            {!isCollapsed && sectionLabel && groupIndex > 0 && (
              <div className="px-3 pt-2 pb-0.5">
                <span className="text-[9px] font-semibold text-slate-400 tracking-[0.15em] uppercase">
                  {sectionLabel}
                </span>
              </div>
            )}
            {isCollapsed && groupIndex > 0 && (
              <div className="py-1.5 px-2">
                <div className="h-px bg-slate-200"></div>
              </div>
            )}

            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id || location.pathname === item.path;
                const isExpanded = expandedMenus[item.id];
                const currentPath = location.pathname;

                let isAnySubActive = false;
                if (item.submenu) {
                  isAnySubActive = item.submenu.some(subItem =>
                    currentPath === subItem.path || currentPath.startsWith(subItem.path)
                  );
                }

                const isHighlighted = isActive || isAnySubActive;

                return (
                  <div key={item.id}>
                    {item.hasSubmenu ? (
                      <>
                        <button
                          onClick={(e) => {
                            if (isCollapsed) {
                              if (item.submenu && item.submenu.length > 0) {
                                handleNavigation(item.submenu[0].path, item.id, true);
                              }
                            } else {
                              toggleSubmenu(item.id, e);
                            }
                          }}
                          className={`
                            w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md
                            transition-all duration-200
                            ${isCollapsed ? 'justify-center' : ''}
                            ${isHighlighted
                              ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 border border-transparent'
                            }
                          `}
                          title={isCollapsed ? item.label : ''}
                        >
                          <Icon className={`h-[16px] w-[16px] flex-shrink-0 ${isHighlighted ? 'text-indigo-600' : ''}`} />

                          {!isCollapsed && (
                            <>
                              <span className="flex-1 text-[13px] font-medium text-left">{item.label}</span>
                              <ChevronDown className={`
                                h-3.5 w-3.5 transition-transform duration-200
                                ${isExpanded ? 'rotate-180 text-slate-600' : 'text-slate-600'}
                              `} />
                            </>
                          )}
                        </button>

                        {/* Submenu */}
                        {!isCollapsed && isExpanded && item.submenu && (
                          <div className="mt-0.5 ml-4 pl-3 border-l border-slate-200 space-y-0.5">
                            {item.submenu.map((subItem) => {
                              const SubIcon = subItem.icon;
                              const isSubActive = currentPath === subItem.path || currentPath.startsWith(subItem.path);

                              return (
                                <button
                                  key={subItem.id}
                                  onClick={() => handleNavigation(subItem.path, item.id, true)}
                                  className={`
                                    w-full flex items-center gap-3 px-3 py-2 rounded-lg
                                    transition-all duration-200 text-sm
                                    ${isSubActive
                                      ? 'text-indigo-700 bg-indigo-50'
                                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                                    }
                                  `}
                                >
                                  <SubIcon className={`h-4 w-4 flex-shrink-0 ${isSubActive ? 'text-indigo-600' : ''}`} />
                                  <span className="font-medium">{subItem.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          if (item.path) {
                            handleNavigation(item.path, item.id, false);
                          }
                        }}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2 rounded-lg
                          transition-all duration-200
                          ${isCollapsed ? 'justify-center' : ''}
                          ${isHighlighted
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                          }
                        `}
                        title={isCollapsed ? item.label : ''}
                      >
                        <div className="relative flex items-center justify-center">
                          {isHighlighted && (
                            <div className="absolute -left-[21px] w-[3px] h-5 bg-indigo-500 rounded-r-full shadow-[0_0_8px_rgba(99,102,241,0.4)]"></div>
                          )}
                          <Icon className={`h-[16px] w-[16px] flex-shrink-0 ${isHighlighted ? 'text-indigo-600' : ''}`} />
                        </div>

                        {!isCollapsed && (
                          <span className="text-[13px] font-medium">{item.label}</span>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-slate-200 p-3 space-y-0.5">
        {/* Settings */}
        <button
          onClick={() => handleNavigation("/settings", "settings")}
          className={`
            w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md
            transition-all duration-200
            ${isCollapsed ? 'justify-center' : ''}
            ${location.pathname === '/settings'
              ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 border border-transparent'
            }
          `}
          title={isCollapsed ? t("nav.settings") || "Settings" : ''}
        >
          <Settings className={`h-[16px] w-[16px] flex-shrink-0 ${location.pathname === '/settings' ? 'text-indigo-600' : ''}`} />
          {!isCollapsed && (
            <span className="text-[13px] font-medium">{t("nav.settings") || "Settings"}</span>
          )}
        </button>

        {/* Docs */}
        <button
          onClick={() => handleNavigation("/docs", "docs")}
          className={`
            w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md
            transition-all duration-200
            ${isCollapsed ? 'justify-center' : ''}
            ${location.pathname === '/docs'
              ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 border border-transparent'
            }
          `}
          title={isCollapsed ? t("nav.docs") : ''}
        >
          <BookOpen className={`h-[16px] w-[16px] flex-shrink-0 ${location.pathname === '/docs' ? 'text-indigo-600' : ''}`} />
          {!isCollapsed && (
            <span className="text-[13px] font-medium">{t("nav.docs")}</span>
          )}
        </button>

        {/* Separator */}
        <div className="pt-1">
          <div className="h-px bg-slate-200 mb-2"></div>
        </div>

        {/* Logout */}
        <button
          onClick={() => {
            logout?.();
            navigate("/login");
          }}
          className={`
            w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md
            transition-all duration-200
            ${isCollapsed ? 'justify-center' : ''}
            text-slate-500 hover:text-red-600 hover:bg-red-50
          `}
          title={isCollapsed ? t("common.logout") : ""}
        >
          <LogOut className="h-[16px] w-[16px] flex-shrink-0" />
          {!isCollapsed && (
            <span className="text-[13px] font-medium">{t("common.logout")}</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default RoleBasedSidebar;
